import { useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/**
 * Hidden file input + icon button that uploads an .xlsx to the given URL.
 * Calls onResult({added, skipped_duplicates, errors, total_rows}) on success.
 */
export default function UploadExcelButton({ url, label = "Upload Excel", testId, onResult }) {
  const inputRef = useRef(null);

  const onChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".xlsx")) {
      toast.error("Only .xlsx files are accepted");
      return;
    }
    const form = new FormData();
    form.append("file", file);
    const t = toast.loading(`Importing ${file.name}…`);
    try {
      const { data } = await api.post(url, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const errs = (data.errors || []).length;
      const detail = errs ? ` (${errs} error${errs > 1 ? "s" : ""})` : "";
      toast.success(
        `${data.added} added, ${data.skipped_duplicates} duplicate${data.skipped_duplicates === 1 ? "" : "s"}${detail}`,
        { id: t, duration: 6000 }
      );
      onResult?.(data);
    } catch (err) {
      toast.error(formatApiError(err), { id: t });
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".xlsx"
        className="hidden"
        onChange={onChange}
        data-testid={`${testId}-input`}
      />
      <Button
        size="sm"
        variant="ghost"
        type="button"
        onClick={() => inputRef.current?.click()}
        title={label}
        data-testid={testId}
      >
        <Upload className="h-4 w-4" />
      </Button>
    </>
  );
}
